"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { fetchCompanyByCnpj as lookupCompanyByCnpj } from "../../utils/cnpjLookup"
import { useNavigate } from "react-router-dom"
import { Icon, Card, Form, Input, Select, Button, Modal, Alert } from "../../components"
import { ResponsiveAlert } from "./Alerts"
import { Api, getSession } from "../../api/Assinafy"
import classNames from "classnames"
import "./styles.scss"
import { currencyFormat, formatBRL, normalizeWhatsAppToE164 } from "../../utils/Formats"
import { maskCpfCnpj, maskPhone, maskCep } from "../../utils/Masks"
import { normalizeCouponCode } from "../../utils/couponBenefits"
import { CouponBenefitPreview } from "./CouponUi"

/**
 * Mês/ano de validade a partir dos dígitos (com ou sem máscara).
 * - 6 dígitos: MM + AAAA
 * - 8 dígitos: DD + MM + AAAA (comum ao colar 01/11/2027)
 * - 4 dígitos: MM + AA (assume 20AA)
 */
function parseCardExpiryDigits(raw) {
  const v = String(raw || "")
    .replace(/\D/g, "")
    .slice(0, 8)
  if (!v.length) {
    return { month: "", year: "", display: "" }
  }

  if (v.length >= 8) {
    const d = Number.parseInt(v.slice(0, 2), 10)
    const m = Number.parseInt(v.slice(2, 4), 10)
    const y = Number.parseInt(v.slice(4, 8), 10)
    if (
      d >= 1 &&
      d <= 31 &&
      m >= 1 &&
      m <= 12 &&
      !Number.isNaN(y) &&
      y >= 2000 &&
      y <= 2099
    ) {
      return {
        month: m,
        year: y,
        display: `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4, 8)}`,
      }
    }
  }

  const six = v.slice(0, 6)
  if (six.length === 6) {
    const m = Number.parseInt(six.slice(0, 2), 10)
    const y = Number.parseInt(six.slice(2, 6), 10)
    return {
      month: m,
      year: y,
      display: `${six.slice(0, 2)}/${six.slice(2, 6)}`,
    }
  }

  if (six.length === 4) {
    const m = Number.parseInt(six.slice(0, 2), 10)
    const yy = Number.parseInt(six.slice(2, 4), 10)
    return {
      month: m,
      year: 2000 + yy,
      display: `${six.slice(0, 2)}/${six.slice(2, 4)}`,
    }
  }

  let display = six
  let month = ""
  let year = ""
  if (six.length >= 3) {
    display = `${six.slice(0, 2)}/${six.slice(2)}`
    month = Number.parseInt(six.slice(0, 2), 10)
  }
  return { month, year, display }
}

const estados = [
  { value: "AC", label: "AC" },
  { value: "AL", label: "AL" },
  { value: "AP", label: "AP" },
  { value: "AM", label: "AM" },
  { value: "BA", label: "BA" },
  { value: "CE", label: "CE" },
  { value: "DF", label: "DF" },
  { value: "ES", label: "ES" },
  { value: "GO", label: "GO" },
  { value: "MA", label: "MA" },
  { value: "MS", label: "MS" },
  { value: "MT", label: "MT" },
  { value: "MG", label: "MG" },
  { value: "PA", label: "PA" },
  { value: "PB", label: "PB" },
  { value: "PR", label: "PR" },
  { value: "PE", label: "PE" },
  { value: "PI", label: "PI" },
  { value: "RJ", label: "RJ" },
  { value: "RN", label: "RN" },
  { value: "RS", label: "RS" },
  { value: "RO", label: "RO" },
  { value: "RR", label: "RR" },
  { value: "SC", label: "SC" },
  { value: "SP", label: "SP" },
  { value: "SE", label: "SE" },
  { value: "TO", label: "TO" },
]

const FiscalForm = ({ cpf, defaults, mode = "billing", onSaveSuccess, saveButtonLabel, loading: externalLoading }) => {
  const navigate = useNavigate()
  const isIdentification = mode === "identification"
  const [openAlert, setOpenAlert] = useState(false)
  const [loading, setLoading] = useState(false)
  const isSaving = externalLoading || loading
  const [wasValidated, setWasValidated] = useState(false)
  const [formError, setFormError] = useState("")
  const [formData, setFormData] = useState({
    name: defaults.name || "",
    cpf: defaults.cpf || "",
    cnpj: defaults.cnpj || "",
    cep: defaults.cep || "",
    city: defaults.city || "",
    state: defaults.state || "",
    address: defaults.address || "",
    number: defaults.number || "",
    neighborhood: defaults.neighborhood || "",
    email: defaults.email || "",
    phone: defaults.phone || "",
  })

  const [errors, setErrors] = useState({
    name: "",
    cpf: "",
    cnpj: "",
    cep: "",
    city: "",
    state: "",
    address: "",
    number: "",
    neighborhood: "",
    email: "",
    phone: "",
  })

  const [cepLoading, setCepLoading] = useState(false)
  const [cnpjLoading, setCnpjLoading] = useState(false)

  // Valor "default" do Input.PhoneInternational (input não-controlado). Só muda
  // no mount e no autofill por CNPJ — nunca a cada tecla —, senão o plugin
  // re-sincronizaria o input a cada tecla, tornando o campo "não digitável".
  const [phoneDefault, setPhoneDefault] = useState(defaults.phone || "")

  // Evita refetch do mesmo CNPJ já consultado.
  const lastCnpjFetchedRef = useRef("")

  const errorMessages = {
    required: "Este campo é obrigatório",
    email: {
      invalid: "Digite um e-mail válido (exemplo: nome@email.com)",
      required: "E-mail é obrigatório",
    },
    taxpayerId: {
      invalid: "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos",
      cpfInvalid: "CPF inválido. Verifique os números digitados",
      cnpjInvalid: "CNPJ inválido. Verifique os números digitados",
    },
    phone: {
      invalid: "Telefone deve ter 10 ou 11 dígitos",
      incomplete: "Digite um telefone completo com DDD",
    },
    cep: {
      invalid: "CEP deve ter 8 dígitos",
      notFound: "CEP não encontrado. Verifique o número digitado",
      error: "Erro ao buscar CEP. Tente novamente",
    },
    name: {
      required: "Nome completo é obrigatório",
      tooShort: "Digite o nome completo (nome e sobrenome)",
    },
  }

  const validateRequired = (field, value) => {
    if (!value || String(value).trim() === "") {
      let message = errorMessages.required
      if (field === "email") message = errorMessages.email.required
      if (field === "name") message = errorMessages.name.required
      if (field === "address") message = errorMessages.required
      if (field === "number") message = "Número é obrigatório"

      setErrors((prev) => ({ ...prev, [field]: message }))
      return false
    }
    setErrors((prev) => ({ ...prev, [field]: "" }))
    return true
  }

  const validateRequiredField = (field) => (value) => validateRequired(field, value)

  const validateEmail = (value) => {
    if (!value || value.trim() === "") {
      setErrors((prev) => ({ ...prev, email: errorMessages.email.required }))
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      setErrors((prev) => ({ ...prev, email: errorMessages.email.invalid }))
      return false
    }

    setErrors((prev) => ({ ...prev, email: "" }))
    return true
  }

  const validateTaxpayerId = (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, [cpf ? "cpf" : "cnpj"]: errorMessages.required }))
      return false
    }

    if (cpf) {
      if (cleanValue.length !== 11) {
        setErrors((prev) => ({ ...prev, cpf: errorMessages.taxpayerId.invalid }))
        return false
      }

      if (/^(\d)\1{10}$/.test(cleanValue)) {
        setErrors((prev) => ({ ...prev, cpf: errorMessages.taxpayerId.cpfInvalid }))
        return false
      }

      let sum = 0
      for (let i = 0; i < 9; i++) {
        sum += Number.parseInt(cleanValue.charAt(i)) * (10 - i)
      }
      let remainder = (sum * 10) % 11
      if (remainder === 10 || remainder === 11) remainder = 0
      if (remainder !== Number.parseInt(cleanValue.charAt(9))) {
        setErrors((prev) => ({ ...prev, cpf: errorMessages.taxpayerId.cpfInvalid }))
        return false
      }

      sum = 0
      for (let i = 0; i < 10; i++) {
        sum += Number.parseInt(cleanValue.charAt(i)) * (11 - i)
      }
      remainder = (sum * 10) % 11
      if (remainder === 10 || remainder === 11) remainder = 0
      if (remainder !== Number.parseInt(cleanValue.charAt(10))) {
        setErrors((prev) => ({ ...prev, cpf: errorMessages.taxpayerId.cpfInvalid }))
        return false
      }
    } else {
      if (cleanValue.length !== 14) {
        setErrors((prev) => ({ ...prev, cnpj: errorMessages.taxpayerId.invalid }))
        return false
      }

      if (/^(\d)\1{13}$/.test(cleanValue)) {
        setErrors((prev) => ({ ...prev, cnpj: errorMessages.taxpayerId.cnpjInvalid }))
        return false
      }

      let sum = 0
      let weight = 2
      for (let i = 11; i >= 0; i--) {
        sum += Number.parseInt(cleanValue.charAt(i)) * weight
        weight++
        if (weight === 10) weight = 2
      }
      let remainder = sum % 11
      const digit1 = remainder < 2 ? 0 : 11 - remainder
      if (digit1 !== Number.parseInt(cleanValue.charAt(12))) {
        setErrors((prev) => ({ ...prev, cnpj: errorMessages.taxpayerId.cnpjInvalid }))
        return false
      }

      sum = 0
      weight = 2
      for (let i = 12; i >= 0; i--) {
        sum += Number.parseInt(cleanValue.charAt(i)) * weight
        weight++
        if (weight === 10) weight = 2
      }
      remainder = sum % 11
      const digit2 = remainder < 2 ? 0 : 11 - remainder
      if (digit2 !== Number.parseInt(cleanValue.charAt(13))) {
        setErrors((prev) => ({ ...prev, cnpj: errorMessages.taxpayerId.cnpjInvalid }))
        return false
      }
    }

    setErrors((prev) => ({ ...prev, [cpf ? "cpf" : "cnpj"]: "" }))
    return true
  }

  const validatePhoneInternational = (value) => {
    const normalized = normalizeWhatsAppToE164(value) || String(value || "").replace(/\D/g, "")

    if (!normalized || normalized.replace(/\D/g, "").length < 10) {
      setErrors((prev) => ({ ...prev, phone: errorMessages.phone.incomplete }))
      return false
    }

    setErrors((prev) => ({ ...prev, phone: "" }))
    return true
  }

  const validatePhone = (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, phone: errorMessages.required }))
      return false
    }

    if (cleanValue.length < 10) {
      setErrors((prev) => ({ ...prev, phone: errorMessages.phone.incomplete }))
      return false
    }

    if (cleanValue.length < 10 || cleanValue.length > 11) {
      setErrors((prev) => ({ ...prev, phone: errorMessages.phone.invalid }))
      return false
    }

    setErrors((prev) => ({ ...prev, phone: "" }))
    return true
  }

  const validateName = (value) => {
    if (!value || value.trim() === "") {
      setErrors((prev) => ({ ...prev, name: errorMessages.name.required }))
      return false
    }

    if (cpf && value.trim().split(" ").length < 2) {
      setErrors((prev) => ({ ...prev, name: errorMessages.name.tooShort }))
      return false
    }

    setErrors((prev) => ({ ...prev, name: "" }))
    return true
  }

  const fetchAddressByCep = async (cep) => {
    try {
      setCepLoading(true)
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json`)

      if (!response.ok) {
        setErrors((prev) => ({ ...prev, cep: errorMessages.cep.error }))
        setWasValidated(true)
        return
      }

      const data = await response.json()

      if (data.erro || !data.uf || !data.localidade) {
        setErrors((prev) => ({ ...prev, cep: errorMessages.cep.notFound }))
        setWasValidated(true)
        return
      }

      setErrors((prev) => ({ ...prev, cep: "" }))

      setFormData((prev) => ({
        ...prev,
        state: data.uf,
        city: data.localidade,
        address: data.logradouro || "",
        neighborhood: data.bairro || "",
      }))
    } catch (error) {
      console.error("Erro ao buscar CEP:", error)
      setErrors((prev) => ({ ...prev, cep: errorMessages.cep.error }))
      setWasValidated(true)
    } finally {
      setCepLoading(false)
    }
  }

  const validatePostalCode = async (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, cep: errorMessages.required }))
      return false
    }

    if (cleanValue.length !== 8) {
      setErrors((prev) => ({ ...prev, cep: errorMessages.cep.invalid }))
      return false
    }

    if (cleanValue.length === 8) {
      await fetchAddressByCep(cleanValue)
    }

    return true
  }

  // Autofill dos dados da empresa via CNPJ (BrasilAPI). Preenche os campos direto
  // via setFormData — não passa pelo onChange do CEP, então NÃO dispara o
  // autocomplete do viacep (evita conflito entre os dois preenchimentos).
  const fetchCompanyByCnpj = async (cnpjDigits) => {
    if (lastCnpjFetchedRef.current === cnpjDigits) return
    lastCnpjFetchedRef.current = cnpjDigits

    try {
      setCnpjLoading(true)
      const data = await lookupCompanyByCnpj(cnpjDigits)

      if (!data) {
        lastCnpjFetchedRef.current = ""
        return
      }

      // Logradouro no campo Endereço e o número no campo próprio (Número).
      const addressLine = data.logradouro ? String(data.logradouro).trim() : ""
      const houseNumber = data.numero ? String(data.numero).trim() : ""

      const phoneDigits = String(data.ddd_telefone_1 || "").replace(/\D/g, "")
      const phoneE164 = phoneDigits ? `+55${phoneDigits}` : ""

      setFormData((prev) => ({
        ...prev,
        name: data.razao_social || prev.name,
        cep: data.cep ? maskCep(data.cep) : prev.cep,
        city: data.municipio || prev.city,
        state: data.uf || prev.state,
        address: addressLine || prev.address,
        number: houseNumber || prev.number,
        neighborhood: data.bairro || prev.neighborhood,
        email: data.email || prev.email,
        phone: phoneE164 || prev.phone,
      }))

      // Atualiza o valor exibido do campo de telefone (input não-controlado).
      if (phoneE164) {
        setPhoneDefault(phoneE164)
      }

      // Limpa os erros dos campos que foram preenchidos.
      setErrors((prev) => ({
        ...prev,
        ...(data.razao_social ? { name: "" } : {}),
        ...(data.cep ? { cep: "" } : {}),
        ...(data.municipio ? { city: "" } : {}),
        ...(data.uf ? { state: "" } : {}),
        ...(addressLine ? { address: "" } : {}),
        ...(houseNumber ? { number: "" } : {}),
        ...(phoneE164 ? { phone: "" } : {}),
      }))
    } catch (error) {
      console.error("Erro ao buscar CNPJ:", error)
      lastCnpjFetchedRef.current = ""
    } finally {
      setCnpjLoading(false)
    }
  }

  const handleInputChange = (field, value, validator = null) => {
    let maskedValue = value

    if (field === "cep") {
      maskedValue = maskCep(value)
    } else if (field === "phone") {
      maskedValue = maskPhone(value)
    } else if (field === "cpf" || field === "cnpj") {
      maskedValue = maskCpfCnpj(value)
    }

    setFormData((prev) => ({
      ...prev,
      [field]: maskedValue,
    }))

    if (validator && typeof validator === "function" && wasValidated) {
      validator(maskedValue)
    }
  }

  const handleFieldBlur = (field, value, validator) => {
    setWasValidated(true)
    if (validator) {
      validator(value)
    } else {
      validateRequired(field, value)
    }
  }

  const handleCepChange = async (e) => {
    const value = e.target.value
    const cleanValue = value.replace(/\D/g, "")
    const maskedValue = maskCep(cleanValue)

    setFormData((prev) => ({ ...prev, cep: cleanValue }))

    if (errors.cep) {
      setErrors((prev) => ({ ...prev, cep: "" }))
    }

    if (cleanValue.length === 8) {
      await validatePostalCode(cleanValue)
    } else if (cleanValue.length > 0 && wasValidated) {
      validatePostalCode(cleanValue)
    }
  }

  // Valida apenas o formato do CEP (8 dígitos), sem chamar o viacep. Usado no
  // save para não sobrescrever o endereço já preenchido (ex.: via CNPJ) —
  // evita o conflito entre os autocompletes de CEP e de CNPJ.
  const validateCepFormat = (value) => {
    const cleanValue = value.replace(/\D/g, "")
    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, cep: errorMessages.required }))
      return false
    }
    if (cleanValue.length !== 8) {
      setErrors((prev) => ({ ...prev, cep: errorMessages.cep.invalid }))
      return false
    }
    setErrors((prev) => ({ ...prev, cep: "" }))
    return true
  }

  const handleSave = async () => {
    setWasValidated(true)
    setFormError("")

    // Valida todos os campos
    const validations = [
      validateName(formData.name),
      validateTaxpayerId(cpf ? formData.cpf : formData.cnpj),
      validateCepFormat(formData.cep),
      validateRequired("city", formData.city),
      validateRequired("state", formData.state),
      validateRequired("address", formData.address),
      validateRequired("number", formData.number),
      validateRequired("neighborhood", formData.neighborhood),
    ]

    if (isIdentification) {
      validations.push(validatePhoneInternational(formData.phone))
    } else {
      validations.push(
        validatePhone(formData.phone),
        validateEmail(formData.email),
      )
    }

    const isValid = validations.every((result) => result !== false)

    if (!isValid) {
      return
    }

    try {
      setLoading(true)

      const sessionUser = getSession()?.user || {}
      const phoneSource = formData.phone || sessionUser.phone_number || sessionUser.telephone || ""
      const normalizedPhone = normalizeWhatsAppToE164(phoneSource) || phoneSource
      // A API espera o telefone em E.164 com o "+" (ex.: "+5548999990000"),
      // mesmo padrão do Input.PhoneInternational (getNumber()). Só cai para
      // dígitos puros no caso raro do normalizador não reconhecer o número.
      const phoneNumberPayload = normalizedPhone.startsWith("+")
        ? normalizedPhone
        : normalizedPhone.replace(/\D/g, "")
      const updateData = {
        name: formData.name,
        taxpayer_id: cpf ? formData.cpf.replace(/\D/g, "") : formData.cnpj.replace(/\D/g, ""),
        email: formData.email || sessionUser.email,
        phone_number: phoneNumberPayload,
        address: {
          postal_code: formData.cep.replace(/\D/g, ""),
          city: formData.city,
          state: formData.state,
          line1: formData.address,
          line2: "",
          house_number: formData.number || "",
          neighborhood: formData.neighborhood || "",
        },
      }

      // Fallback: se o número não veio no campo próprio mas o endereço tem
      // vírgula ("Rua X, 123"), extrai dali (compat. com dados antigos/autofill).
      if (!updateData.address.house_number) {
        const addressParts = formData.address.split(",")
        if (addressParts.length > 1) {
          updateData.address.line1 = addressParts[0].trim()
          updateData.address.house_number = addressParts[1].trim()
          if (addressParts.length > 2) {
            updateData.address.line2 = addressParts.slice(2).join(",").trim()
          }
        }
      }

      const response = await Api("customer/update")(updateData)

      if (response && response.status === 200) {
        if (onSaveSuccess) {
          await onSaveSuccess()
        } else {
          setOpenAlert(true)
        }
      } else {
        throw new Error("Erro ao atualizar dados")
      }
    } catch (error) {
      console.error("Erro ao salvar dados fiscais:", error)
      setFormError(
        error?.response?.data?.message ||
          "Não foi possível salvar os dados. Verifique os campos e tente novamente.",
      )
    } finally {
      setLoading(false)
    }
  }

  const nameLabel = isIdentification ? (cpf ? "Nome" : "Nome da Empresa") : "Nome"

  const taxpayerField = cpf ? (
    <Form.Group>
      <Input.Cpf
        label="CPF"
        value={formData.cpf}
        onChange={(e) => handleInputChange("cpf", e.target.value, validateTaxpayerId)}
        onBlur={(e) => handleFieldBlur("cpf", e.target.value, validateTaxpayerId)}
        disabled={isSaving}
        required
        className={errors.cpf ? "is-invalid" : ""}
      />
      {errors.cpf && <div className="help-block">{errors.cpf}</div>}
    </Form.Group>
  ) : (
    <Form.Group>
      <Input
        label="CNPJ"
        value={formData.cnpj}
        onChange={(e) => {
          handleInputChange("cnpj", e.target.value, validateTaxpayerId)
          const digits = e.target.value.replace(/\D/g, "").slice(0, 14)
          if (digits.length === 14) {
            fetchCompanyByCnpj(digits)
          } else {
            lastCnpjFetchedRef.current = ""
          }
        }}
        onBlur={(e) => handleFieldBlur("cnpj", e.target.value, validateTaxpayerId)}
        disabled={isSaving || cnpjLoading}
        required
        className={`cnpj-search${errors.cnpj ? " is-invalid" : ""}`}
      />
      {cnpjLoading && (
        <small style={{ color: "#666", fontSize: "12px" }}>Buscando dados da empresa...</small>
      )}
      {errors.cnpj && <div className="help-block">{errors.cnpj}</div>}
    </Form.Group>
  )

  const nameField = (
    <Form.Group>
      <Input
        label={nameLabel}
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value, validateName)}
        onBlur={(e) => handleFieldBlur("name", e.target.value, validateName)}
        disabled={isSaving}
        required
        className={errors.name ? "is-invalid" : ""}
      />
      {errors.name && <div className="help-block">{errors.name}</div>}
    </Form.Group>
  )

  const addressField = (
    <div className="shared-row">
      <Form.Group>
        <Input
          label="Endereço"
          value={formData.address}
          onChange={(e) => handleInputChange("address", e.target.value, validateRequiredField("address"))}
          onBlur={(e) => handleFieldBlur("address", e.target.value, validateRequiredField("address"))}
          disabled={isSaving}
          required
          className={errors.address ? "is-invalid" : ""}
        />
        {errors.address && <div className="help-block">{errors.address}</div>}
      </Form.Group>
      <Form.Group style={{ width: "140px" }}>
        <Input
          label="Número"
          value={formData.number}
          onChange={(e) => handleInputChange("number", e.target.value, validateRequiredField("number"))}
          onBlur={(e) => handleFieldBlur("number", e.target.value, validateRequiredField("number"))}
          disabled={isSaving}
          required
          className={errors.number ? "is-invalid" : ""}
        />
        {errors.number && <div className="help-block">{errors.number}</div>}
      </Form.Group>
    </div>
  )

  const cepField = (
    <Form.Group>
      <Input
        label="CEP"
        value={formData.cep}
        onChange={(e) => handleInputChange("cep", e.target.value)}
        onBlur={(e) => handleFieldBlur("cep", e.target.value, validatePostalCode)}
        disabled={isSaving}
        required
        className={errors.cep ? "is-invalid" : ""}
      />
      {errors.cep && <div className="help-block">{errors.cep}</div>}
    </Form.Group>
  )

  const neighborhoodField = (
    <Form.Group>
      <Input
        label="Bairro"
        value={formData.neighborhood}
        onChange={(e) => handleInputChange("neighborhood", e.target.value, validateRequiredField("neighborhood"))}
        onBlur={(e) => handleFieldBlur("neighborhood", e.target.value, validateRequiredField("neighborhood"))}
        disabled={isSaving}
        required
        className={errors.neighborhood ? "is-invalid" : ""}
      />
      {errors.neighborhood && <div className="help-block">{errors.neighborhood}</div>}
    </Form.Group>
  )

  const cityStateFields = (
    <div className="shared-row">
      <Form.Group>
        <Input
          label="Cidade"
          value={formData.city}
          onChange={(e) => handleInputChange("city", e.target.value, validateRequiredField("city"))}
          onBlur={(e) => handleFieldBlur("city", e.target.value, validateRequiredField("city"))}
          disabled={isSaving}
          required
          className={errors.city ? "is-invalid" : ""}
        />
        {errors.city && <div className="help-block">{errors.city}</div>}
      </Form.Group>
      <Form.Group style={{ width: "321px" }}>
        <Select
          label="UF - Estado"
          options={estados}
          value={formData.state}
          onChange={(e) => handleInputChange("state", e.target.value, validateRequiredField("state"))}
          onBlur={(e) => handleFieldBlur("state", e.target.value, validateRequiredField("state"))}
          disabled={isSaving}
          required
          className={errors.state ? "is-invalid" : ""}
        />
        {errors.state && <div className="help-block">{errors.state}</div>}
      </Form.Group>
    </div>
  )

  const phoneField = isIdentification ? (
    <Form.Group className={errors.phone ? "is-invalid" : ""}>
      <Input.PhoneInternational
        label="Telefone"
        name="phone"
        defaultValue={phoneDefault}
        onChange={(e) => handleInputChange("phone", e.target.value, validatePhoneInternational)}
        onBlur={(e) => handleFieldBlur("phone", e.target.value, validatePhoneInternational)}
        disabled={isSaving}
      />
      {errors.phone && <div className="help-block">{errors.phone}</div>}
    </Form.Group>
  ) : (
    <Form.Group>
      <Input
        label="Telefone"
        value={formData.phone}
        onChange={(e) => handleInputChange("phone", e.target.value, validatePhone)}
        onBlur={(e) => handleFieldBlur("phone", e.target.value, validatePhone)}
        disabled={isSaving}
        placeholder="(00) 00000-0000"
        required
        className={errors.phone ? "is-invalid" : ""}
      />
      {errors.phone && <div className="help-block">{errors.phone}</div>}
    </Form.Group>
  )

  return (
    <>
      <Form className={`billing ${wasValidated ? "was-validated" : ""}`}>
        {formError && (
          <Alert variant="danger" onClose={() => setFormError("")} dismissible>
            {formError}
          </Alert>
        )}
        {isIdentification ? (
          <>
            {taxpayerField}
            {nameField}
            {phoneField}
            {cepField}
            {addressField}
            {neighborhoodField}
            {cityStateFields}
          </>
        ) : (
          <>
            {nameField}
            {taxpayerField}
            {cepField}
            {cityStateFields}
            {addressField}
            {neighborhoodField}
            {phoneField}
            <Form.Group>
              <Input.Email
                label="E-mail de recibo"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value, validateEmail)}
                onBlur={(e) => handleFieldBlur("email", e.target.value, validateEmail)}
                disabled={isSaving}
                required
                className={errors.email ? "is-invalid" : ""}
              />
              {errors.email && <div className="help-block">{errors.email}</div>}
              <Form.Text>Esse e-mail receberá a fatura e Nota Fiscal</Form.Text>
            </Form.Group>
          </>
        )}

        <div className={`button-row ${isIdentification ? "button-row-centered" : ""}`}>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Salvando..." : saveButtonLabel || "Salvar alterações"}
          </Button>
          {!isIdentification && (
            <Button
              variant="border"
              disabled={isSaving}
              onClick={() => navigate("/dashboard/workspace/plan")}
              type="button"
            >
              Cancelar
            </Button>
          )}
        </div>
      </Form>
      {!isIdentification && (
        <ResponsiveAlert show={openAlert} onClose={() => setOpenAlert(false)} text="Dados atualizados com sucesso" />
      )}
    </>
  )
}

const CreditForm = ({ defaults = {}, loading, onFormChange, forceValidation = false, ...props }) => {
  const { className } = props
  const newClassName = classNames(className, "credit-form", "billing")

  const [wasValidated, setWasValidated] = useState(false)

  const [formData, setFormData] = useState({
    number: defaults.number || "",
    expiration_date: defaults.expiration_date || "",
    expiration_month: defaults.expiration_month || "",
    expiration_year: defaults.expiration_year || "",
    security_code: defaults.security_code || "",
    name_on_card: defaults.name_on_card || "",
    holder_name: defaults.holder_name || "",
    holder_email: defaults.holder_email || "",
    holder_taxpayer_id: defaults.holder_taxpayer_id || "",
    holder_phone: defaults.holder_phone || "",
    postal_code: defaults.postal_code || "",
    state: defaults.state || "",
    city: defaults.city || "",
    address_line1: defaults.address_line1 || "",
    address_line2: defaults.address_line2 || "",
    house_number: defaults.house_number || "",
    neighborhood: defaults.neighborhood || "",
  })

  const [errors, setErrors] = useState({
    number: "",
    expiration_date: "",
    security_code: "",
    name_on_card: "",
    holder_name: "",
    holder_email: "",
    holder_taxpayer_id: "",
    holder_phone: "",
    postal_code: "",
    state: "",
    city: "",
    address_line1: "",
    house_number: "",
    neighborhood: "",
  })

  const [cepLoading, setCepLoading] = useState(false)

  const errorMessages = {
    required: "Este campo é obrigatório",
    cardNumber: {
      invalid: "Número do cartão inválido. Verifique os dígitos digitados",
      incomplete: "Número do cartão deve ter 16 dígitos",
      luhn: "Este número de cartão não é válido",
    },
    expiration: {
      invalidMonth: "Mês deve estar entre 01 e 12",
      expired: "Este cartão já expirou. Use um cartão válido",
      invalidYear: "Ano inválido",
    },
    cvv: {
      invalid: "CVV deve ter 3 ou 4 dígitos",
      incomplete: "Digite o código de segurança completo",
    },
    email: {
      invalid: "Digite um e-mail válido (exemplo: nome@email.com)",
      required: "E-mail é obrigatório para envio da nota fiscal",
    },
    taxpayerId: {
      invalid: "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos",
      cpfInvalid: "CPF inválido. Verifique os números digitados",
      cnpjInvalid: "CNPJ inválido. Verifique os números digitados",
    },
    phone: {
      invalid: "Telefone deve ter 10 ou 11 dígitos",
      incomplete: "Digite um telefone completo com DDD",
    },
    cep: {
      invalid: "CEP deve ter 8 dígitos",
      notFound: "CEP não encontrado. Verifique o número digitado",
      error: "Erro ao buscar CEP. Tente novamente",
    },
    name: {
      required: "Nome completo é obrigatório",
      tooShort: "Digite o nome completo (nome e sobrenome)",
    },
    address: {
      required: "Endereço é obrigatório",
      number: "Número do endereço é obrigatório",
    },
  }

  const validateRequired = (field, value) => {
    if (!value || String(value).trim() === "") {
      let message = errorMessages.required
      if (field === "holder_email") message = errorMessages.email.required
      if (field === "holder_name") message = errorMessages.name.required
      if (field === "address_line1") message = errorMessages.address.required
      if (field === "house_number") message = errorMessages.address.number

      setErrors((prev) => ({ ...prev, [field]: message }))
      return false
    }
    setErrors((prev) => ({ ...prev, [field]: "" }))
    return true
  }

  const validateRequiredField = (field) => (value) => validateRequired(field, value)

  const validateCardNumber = (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, number: errorMessages.required }))
      return false
    }

    if (cleanValue.length < 16) {
      setErrors((prev) => ({ ...prev, number: errorMessages.cardNumber.incomplete }))
      return false
    }

    if (cleanValue.length !== 16) {
      setErrors((prev) => ({ ...prev, number: errorMessages.cardNumber.invalid }))
      return false
    }

    let sum = 0
    let double = false
    for (let i = cleanValue.length - 1; i >= 0; i--) {
      let digit = Number.parseInt(cleanValue.charAt(i), 10)
      if (double) {
        digit *= 2
        if (digit > 9) digit -= 9
      }
      sum += digit
      double = !double
    }

    if (sum % 10 !== 0) {
      setErrors((prev) => ({ ...prev, number: errorMessages.cardNumber.luhn }))
      return false
    }

    setErrors((prev) => ({ ...prev, number: "" }))
    return true
  }

  const validateExpirationDate = (month, year) => {
    if (!month || !year) {
      setErrors((prev) => ({ ...prev, expiration_date: errorMessages.required }))
      return false
    }

    if (month < 1 || month > 12) {
      setErrors((prev) => ({ ...prev, expiration_date: errorMessages.expiration.invalidMonth }))
      return false
    }

    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      setErrors((prev) => ({ ...prev, expiration_date: errorMessages.expiration.expired }))
      return false
    }

    setErrors((prev) => ({ ...prev, expiration_date: "" }))
    return true
  }

  const validateSecurityCode = (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, security_code: errorMessages.required }))
      return false
    }

    if (cleanValue.length < 3) {
      setErrors((prev) => ({ ...prev, security_code: errorMessages.cvv.incomplete }))
      return false
    }

    if (cleanValue.length < 3 || cleanValue.length > 4) {
      setErrors((prev) => ({ ...prev, security_code: errorMessages.cvv.invalid }))
      return false
    }

    setErrors((prev) => ({ ...prev, security_code: "" }))
    return true
  }

  const validateEmail = (value) => {
    if (!value || value.trim() === "") {
      setErrors((prev) => ({ ...prev, holder_email: errorMessages.email.required }))
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      setErrors((prev) => ({ ...prev, holder_email: errorMessages.email.invalid }))
      return false
    }

    setErrors((prev) => ({ ...prev, holder_email: "" }))
    return true
  }

  const validateTaxpayerId = (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.required }))
      return false
    }

    if (cleanValue.length !== 11 && cleanValue.length !== 14) {
      setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.invalid }))
      return false
    }

    if (cleanValue.length === 11) {
      if (/^(\d)\1{10}$/.test(cleanValue)) {
        setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.cpfInvalid }))
        return false
      }

      let sum = 0
      for (let i = 0; i < 9; i++) {
        sum += Number.parseInt(cleanValue.charAt(i)) * (10 - i)
      }
      let remainder = (sum * 10) % 11
      if (remainder === 10 || remainder === 11) remainder = 0
      if (remainder !== Number.parseInt(cleanValue.charAt(9))) {
        setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.cpfInvalid }))
        return false
      }

      sum = 0
      for (let i = 0; i < 10; i++) {
        sum += Number.parseInt(cleanValue.charAt(i)) * (11 - i)
      }
      remainder = (sum * 10) % 11
      if (remainder === 10 || remainder === 11) remainder = 0
      if (remainder !== Number.parseInt(cleanValue.charAt(10))) {
        setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.cpfInvalid }))
        return false
      }
    }

    if (cleanValue.length === 14) {
      if (/^(\d)\1{13}$/.test(cleanValue)) {
        setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.cnpjInvalid }))
        return false
      }

      let sum = 0
      let weight = 2
      for (let i = 11; i >= 0; i--) {
        sum += Number.parseInt(cleanValue.charAt(i)) * weight
        weight++
        if (weight === 10) weight = 2
      }
      let remainder = sum % 11
      const digit1 = remainder < 2 ? 0 : 11 - remainder
      if (digit1 !== Number.parseInt(cleanValue.charAt(12))) {
        setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.cnpjInvalid }))
        return false
      }

      sum = 0
      weight = 2
      for (let i = 12; i >= 0; i--) {
        sum += Number.parseInt(cleanValue.charAt(i)) * weight
        weight++
        if (weight === 10) weight = 2
      }
      remainder = sum % 11
      const digit2 = remainder < 2 ? 0 : 11 - remainder
      if (digit2 !== Number.parseInt(cleanValue.charAt(13))) {
        setErrors((prev) => ({ ...prev, holder_taxpayer_id: errorMessages.taxpayerId.cnpjInvalid }))
        return false
      }
    }

    setErrors((prev) => ({ ...prev, holder_taxpayer_id: "" }))
    return true
  }

  const validatePhone = (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, holder_phone: errorMessages.required }))
      return false
    }

    if (cleanValue.length < 10) {
      setErrors((prev) => ({ ...prev, holder_phone: errorMessages.phone.incomplete }))
      return false
    }

    if (cleanValue.length < 10 || cleanValue.length > 11) {
      setErrors((prev) => ({ ...prev, holder_phone: errorMessages.phone.invalid }))
      return false
    }

    setErrors((prev) => ({ ...prev, holder_phone: "" }))
    return true
  }

  const validateName = (value) => {
    if (!value || value.trim() === "") {
      setErrors((prev) => ({ ...prev, holder_name: errorMessages.name.required }))
      return false
    }

    if (value.trim().split(" ").length < 2) {
      setErrors((prev) => ({ ...prev, holder_name: errorMessages.name.tooShort }))
      return false
    }

    setErrors((prev) => ({ ...prev, holder_name: "" }))
    return true
  }

  const fetchAddressByCep = async (cep) => {
    try {
      setCepLoading(true)
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json`)

      if (!response.ok) {
        setErrors((prev) => ({ ...prev, postal_code: errorMessages.cep.error }))
        setWasValidated(true)
        return
      }

      const data = await response.json()

      if (data.erro || !data.uf || !data.localidade) {
        setErrors((prev) => ({ ...prev, postal_code: errorMessages.cep.notFound }))
        setWasValidated(true)
        return
      }

      setErrors((prev) => ({ ...prev, postal_code: "" }))

      setFormData((prev) => ({
        ...prev,
        state: data.uf,
        city: data.localidade,
        address_line1: data.logradouro || "",
        neighborhood: data.bairro || "",
      }))
    } catch (error) {
      console.error("Erro ao buscar CEP:", error)
      setErrors((prev) => ({ ...prev, postal_code: errorMessages.cep.error }))
      setWasValidated(true)
    } finally {
      setCepLoading(false)
    }
  }

  const validatePostalCode = async (value) => {
    const cleanValue = value.replace(/\D/g, "")

    if (cleanValue.length === 0) {
      setErrors((prev) => ({ ...prev, postal_code: errorMessages.required }))
      return false
    }

    if (cleanValue.length !== 8) {
      setErrors((prev) => ({ ...prev, postal_code: errorMessages.cep.invalid }))
      return false
    }

    if (cleanValue.length === 8) {
      await fetchAddressByCep(cleanValue)
    }

    return true
  }

  const validateNameOnCard = (value) => validateRequired("name_on_card", value)
  const validateHouseNumber = (value) => validateRequired("house_number", value)

  const checkFormValidity = useCallback(() => {
    const requiredFields = [
      "number",
      "expiration_month",
      "expiration_year",
      "security_code",
      "name_on_card",
      "holder_name",
      "holder_email",
      "holder_taxpayer_id",
      "holder_phone",
      "postal_code",
      "state",
      "city",
      "address_line1",
      "house_number",
      "neighborhood",
    ]

    for (const field of requiredFields) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        return false
      }
    }

    const errorFieldKeys = [
      "number",
      "expiration_date",
      "security_code",
      "name_on_card",
      "holder_name",
      "holder_email",
      "holder_taxpayer_id",
      "holder_phone",
      "postal_code",
      "state",
      "city",
      "address_line1",
      "house_number",
      "neighborhood",
    ]
    for (const field of errorFieldKeys) {
      if (errors[field]) {
        return false
      }
    }

    return true
  }, [formData, errors])

  const formChangeData = useMemo(
    () => ({
      formData,
      isValid: checkFormValidity(),
    }),
    [formData, checkFormValidity],
  )

  const onFormChangeRef = useRef(onFormChange)

  useEffect(() => {
    onFormChangeRef.current = onFormChange
  }, [onFormChange])

  useEffect(() => {
    if (onFormChangeRef.current) {
      onFormChangeRef.current(formChangeData)
    }
  }, [formChangeData])

  useEffect(() => {
    if (forceValidation) {
      setWasValidated(true)

      validateCardNumber(formData.number)
      validateExpirationDate(formData.expiration_month, formData.expiration_year)
      validateSecurityCode(formData.security_code)
      validateNameOnCard(formData.name_on_card)
      validateRequired("holder_name", formData.holder_name)
      validateEmail(formData.holder_email)
      validateTaxpayerId(formData.holder_taxpayer_id)
      validatePostalCode(formData.postal_code)
      validateRequired("address_line1", formData.address_line1)
      validateHouseNumber(formData.house_number)
      validateRequired("neighborhood", formData.neighborhood)
      validateRequired("city", formData.city)
      validateRequired("state", formData.state)
      validatePhone(formData.holder_phone)
    }
  }, [forceValidation])

  const handleFieldBlur = (field, value, validator) => {
    setWasValidated(true)
    if (validator) {
      validator(value)
    } else {
      validateRequired(field, value)
    }
  }

  const maskOnlyLetters = (value) => {
    return value.replace(/[^a-zA-ZÀ-ÿ\u00f1\u00d1\s]/g, "")
  }

  const handleInputChange = (field, value, validator = null) => {
    let maskedValue = value

    if (field === "holder_taxpayer_id") {
      maskedValue = maskCpfCnpj(value)
    } else if (field === "holder_phone") {
      maskedValue = maskPhone(value)
    } else if (field === "postal_code") {
      maskedValue = maskCep(value)
    }

    setFormData((prev) => ({
      ...prev,
      [field]: maskedValue,
    }))

    if (validator && typeof validator === "function") {
      validator(maskedValue)
    }
  }

  const handleCardNumberChange = (e) => {
    const value = e.target.value
    const cleanValue = value.replace(/\D/g, "")
    const limitedValue = cleanValue.substring(0, 16)
    const formattedValue = limitedValue.replace(/(\d{4})(?=\d)/g, "$1 ")
    handleInputChange("number", formattedValue, wasValidated ? validateCardNumber : null)
  }

  const handleExpirationDateChange = (e) => {
    const { month, year, display } = parseCardExpiryDigits(e.target.value)

    setFormData((prev) => ({
      ...prev,
      expiration_date: display,
      expiration_month: month,
      expiration_year: year,
    }))

    if (wasValidated) {
      validateExpirationDate(month, year)
    }
  }

  const handleCepChange = async (e) => {
    const value = e.target.value
    const cleanValue = value.replace(/\D/g, "")
    const maskedValue = maskCep(cleanValue)

    setFormData((prev) => ({ ...prev, postal_code: cleanValue }))

    if (errors.postal_code) {
      setErrors((prev) => ({ ...prev, postal_code: "" }))
    }

    if (cleanValue.length === 8) {
      await validatePostalCode(cleanValue)
    } else if (cleanValue.length > 0 && wasValidated) {
      validatePostalCode(cleanValue)
    }
  }

  return (
    <Form {...props} className={`${newClassName} ${wasValidated ? "was-validated" : ""}`}>
      <p>Dados do cartão</p>
      <Form.Group>
        <Input
          label="Número do cartão"
          value={formData.number}
          onChange={handleCardNumberChange}
          onBlur={(e) => handleFieldBlur("number", e.target.value, validateCardNumber)}
          disabled={loading}
          placeholder="0000 0000 0000 0000"
          className={errors.number ? "is-invalid" : ""}
          required
        />
        {errors.number && <div className="help-block">{errors.number}</div>}
      </Form.Group>
      <div className="shared-row">
        <Form.Group>
          <Input
            label="Data de validade"
            placeholder="MM/AAAA ou DD/MM/AAAA"
            value={formData.expiration_date}
            onChange={handleExpirationDateChange}
            onBlur={(e) => {
              const { month, year } = parseCardExpiryDigits(e.target.value)
              handleFieldBlur("expiration_date", e.target.value, () =>
                validateExpirationDate(month, year),
              )
            }}
            disabled={loading}
            className={errors.expiration_date ? "is-invalid" : ""}
            required
          />
          {errors.expiration_date && <div className="help-block">{errors.expiration_date}</div>}
        </Form.Group>
        <Form.Group>
          <Input
            label="Código de segurança (CVV)"
            value={formData.security_code}
            onChange={(e) =>
              handleInputChange("security_code", e.target.value, wasValidated ? validateSecurityCode : null)
            }
            onBlur={(e) => handleFieldBlur("security_code", e.target.value, validateSecurityCode)}
            disabled={loading}
            maxLength={3}
            className={errors.security_code ? "is-invalid" : ""}
            required
          />
          {errors.security_code && <div className="help-block">{errors.security_code}</div>}
        </Form.Group>
      </div>
      <Form.Group>
        <Input
          label="Nome no cartão (Idêntico ao escrito no cartão)"
          value={formData.name_on_card}
          onChange={(e) => handleInputChange("name_on_card", e.target.value, wasValidated ? validateNameOnCard : null)}
          onBlur={(e) => handleFieldBlur("name_on_card", e.target.value, validateNameOnCard)}
          disabled={loading}
          className={errors.name_on_card ? "is-invalid" : ""}
          required
        />
        {errors.name_on_card && <div className="help-block">{errors.name_on_card}</div>}
      </Form.Group>

      <p>Dados do titular do cartão</p>
      <Form.Group>
        <Input
          label="Nome completo do titular"
          value={formData.holder_name}
          onChange={(e) => handleInputChange("holder_name", e.target.value, wasValidated ? validateName : null)}
          onBlur={(e) => handleFieldBlur("holder_name", e.target.value, validateName)}
          disabled={loading}
          className={errors.holder_name ? "is-invalid" : ""}
          required
        />
        {errors.holder_name && <div className="help-block">{errors.holder_name}</div>}
      </Form.Group>
      <Form.Group>
        <Input
          label="CPF/CNPJ do titular do cartão"
          value={formData.holder_taxpayer_id}
          onChange={(e) =>
            handleInputChange("holder_taxpayer_id", e.target.value, wasValidated ? validateTaxpayerId : null)
          }
          onBlur={(e) => handleFieldBlur("holder_taxpayer_id", e.target.value, validateTaxpayerId)}
          disabled={loading}
          className={errors.holder_taxpayer_id ? "is-invalid" : ""}
          maxLength="18"
          required
        />
        {errors.holder_taxpayer_id && <div className="help-block">{errors.holder_taxpayer_id}</div>}
      </Form.Group>
      <Form.Group>
        <Input.Email
          label="E-mail do titular"
          value={formData.holder_email}
          onChange={(e) => handleInputChange("holder_email", e.target.value, wasValidated ? validateEmail : null)}
          onBlur={(e) => handleFieldBlur("holder_email", e.target.value, validateEmail)}
          disabled={loading}
          className={errors.holder_email ? "is-invalid" : ""}
          required
        />
        {errors.holder_email && <div className="help-block">{errors.holder_email}</div>}
      </Form.Group>
      <Form.Group>
        <Input
          label="Telefone"
          placeholder="(00) 00000-0000"
          value={formData.holder_phone}
          onChange={(e) => handleInputChange("holder_phone", e.target.value, wasValidated ? validatePhone : null)}
          onBlur={(e) => handleFieldBlur("holder_phone", e.target.value, validatePhone)}
          disabled={loading}
          maxLength={15}
          className={errors.holder_phone ? "is-invalid" : ""}
          required
        />
        {errors.holder_phone && <div className="help-block">{errors.holder_phone}</div>}
      </Form.Group>

      <p>Endereço da fatura</p>
      <Form.Group>
        <Input
          label="CEP"
          value={maskCep(formData.postal_code)}
          onChange={handleCepChange}
          onBlur={(e) => handleFieldBlur("postal_code", e.target.value, validatePostalCode)}
          disabled={loading || cepLoading}
          placeholder="00000-000"
          maxLength="9"
          className={errors.postal_code ? "is-invalid" : ""}
          required
        />
        {cepLoading && <small style={{ color: "#666", fontSize: "12px" }}>Buscando endereço...</small>}
        {errors.postal_code && <div className="help-block">{errors.postal_code}</div>}
      </Form.Group>
      <div className="shared-row">
        <Form.Group>
          <Select
            label="UF - Estado"
            options={estados}
            value={formData.state}
            onChange={(e) =>
              handleInputChange("state", e.target.value, wasValidated ? validateRequiredField("state") : null)
            }
            onBlur={(e) => handleFieldBlur("state", e.target.value, validateRequiredField("state"))}
            disabled={loading}
            className={errors.state ? "is-invalid" : ""}
            required
          />
          {errors.state && <div className="help-block">{errors.state}</div>}
        </Form.Group>
        <Form.Group>
          <Input
            label="Cidade"
            value={formData.city}
            onChange={(e) =>
              handleInputChange("city", e.target.value, wasValidated ? validateRequiredField("city") : null)
            }
            onBlur={(e) => handleFieldBlur("city", e.target.value, validateRequiredField("city"))}
            disabled={loading}
            className={errors.city ? "is-invalid" : ""}
            required
          />
          {errors.city && <div className="help-block">{errors.city}</div>}
        </Form.Group>
      </div>
      <Form.Group>
        <Input
          label="Endereço (Rua, Avenida, etc.)"
          value={formData.address_line1}
          onChange={(e) =>
            handleInputChange(
              "address_line1",
              e.target.value,
              wasValidated ? validateRequiredField("address_line1") : null,
            )
          }
          onBlur={(e) => handleFieldBlur("address_line1", e.target.value, validateRequiredField("address_line1"))}
          disabled={loading}
          className={errors.address_line1 ? "is-invalid" : ""}
          required
        />
        {errors.address_line1 && <div className="help-block">{errors.address_line1}</div>}
      </Form.Group>
      <div className="shared-row">
        <Form.Group>
          <Input
            label="Complemento (opcional)"
            value={formData.address_line2}
            onChange={(e) => handleInputChange("address_line2", e.target.value)}
            disabled={loading}
          />
        </Form.Group>
        <Form.Group style={{ width: "140px" }}>
          <Input
            label="Número"
            value={formData.house_number}
            onChange={(e) =>
              handleInputChange("house_number", e.target.value, wasValidated ? validateHouseNumber : null)
            }
            onBlur={(e) => handleFieldBlur("house_number", e.target.value, validateHouseNumber)}
            disabled={loading}
            className={errors.house_number ? "is-invalid" : ""}
            required
          />
          {errors.house_number && <div className="help-block">{errors.house_number}</div>}
        </Form.Group>
      </div>
      <Form.Group>
          <Input
            label="Bairro"
            value={formData.neighborhood}
            onChange={(e) =>
              handleInputChange(
                "neighborhood",
                e.target.value,
                wasValidated ? validateRequiredField("neighborhood") : null,
              )
            }
            onBlur={(e) => handleFieldBlur("neighborhood", e.target.value, validateRequiredField("neighborhood"))}
          disabled={loading}
          className={errors.neighborhood ? "is-invalid" : ""}
          required
        />
        {errors.neighborhood && <div className="help-block">{errors.neighborhood}</div>}
      </Form.Group>
    </Form>
  )
}

const CupomModal = ({
  show,
  onClose,
  cupom,
  context = "credits",
  setCupomName,
  setCupom,
  setCupomValue,
  onApplyCoupon,
  onValidateCoupon,
  onConfirmCoupon,
}) => {
  const [input, setInput] = useState(cupom)
  const [validity, setValidity] = useState("")
  const [infoText, setInfoText] = useState("")
  const [loading, setLoading] = useState(false)
  const [validatedCoupon, setValidatedCoupon] = useState(null)

  const validateHandler = onValidateCoupon || onApplyCoupon

  useEffect(() => {
    if (show) {
      setInput(cupom || "")
      setValidity("")
      setInfoText("")
      setValidatedCoupon(null)
    }
  }, [show, cupom])

  const handleInputChange = (event) => {
    setInput(event.target.value)
    setValidity("")
    setInfoText("")
    setValidatedCoupon(null)
  }

  const validateCoupon = async () => {
    const couponCode = input?.trim()
    if (!couponCode) {
      setValidity("warning")
      setInfoText("Informe o código do cupom")
      return false
    }

    if (!validateHandler) {
      return false
    }

    setLoading(true)
    try {
      const result = await validateHandler(couponCode)
      if (result?.success) {
        const applied = {
          ...(result.coupon || {}),
          code: result.code || normalizeCouponCode(couponCode),
        }
        setValidatedCoupon(applied)
        setValidity("success")
        setInfoText("Cupom válido")
        return true
      }

      setValidatedCoupon(null)
      setValidity("warning")
      setInfoText(result?.message || "Cupom inválido")
      return false
    } catch (error) {
      setValidatedCoupon(null)
      setValidity("warning")
      setInfoText(error?.response?.data?.message || "Não foi possível validar o cupom")
      return false
    } finally {
      setLoading(false)
    }
  }

  const confirmCoupon = (appliedCoupon) => {
    if (onConfirmCoupon) {
      onConfirmCoupon(appliedCoupon)
    }

    if (setCupomName) {
      setCupomName(appliedCoupon.code)
    }
    if (setCupom) {
      setCupom(true)
    }
    if (setCupomValue) {
      setCupomValue(0)
    }

    onClose()
  }

  const handlePrimaryAction = async () => {
    const normalizedInput = normalizeCouponCode(input)
    const isSameValidatedCoupon =
      validatedCoupon && validatedCoupon.code === normalizedInput

    if (!isSameValidatedCoupon) {
      await validateCoupon()
      return
    }

    confirmCoupon(validatedCoupon)
  }

  return (
    <Modal.Alert
      onClose={onClose}
      className="cupom-modal"
      show={show}
      title="Adicionar cupom"
      description="Digite o código e veja o que você ganha."
    >
      <div className="modal-body">
        <Form className="billing">
          <Form.Group className={`w-100 ${validity}`}>
            <Input
              label="Insira o código do cupom"
              value={input}
              onChange={handleInputChange}
            />
            {validity === "success" && <Icon id="icon-check" />}
            {validity === "warning" && <Icon id="icon-warning" />}
            <Form.Text>{infoText || "　"}</Form.Text>
          </Form.Group>
        </Form>

        {validatedCoupon && (
          <CouponBenefitPreview coupon={validatedCoupon} context={context} />
        )}

        <div className="d-flex gap-3 w-100 mt-3 justify-content-end">
          <Button variant="border" className="text-uppercase d-none d-lg-block" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handlePrimaryAction}
            className="text-uppercase"
            disabled={loading}
          >
            {loading
              ? "Validando..."
              : validatedCoupon
                ? context === "redeem" ? "Aplicar cupom" : "Adicionar cupom"
                : "Validar cupom"}
          </Button>
        </div>
      </div>
    </Modal.Alert>
  )
}

const PaymentCard = ({ topcontent, middlecontent, total, totalLabel = "Total", paymentDate, className }) => {
  const newClassName = classNames(className, "payment-card")

  return (
    <Card className={newClassName}>
      <div className="payment-card-top">{topcontent}</div>
      <div className="payment-card-middle">{middlecontent}</div>
      <div className="payment-card-bottom">
        <div className="payment-card-summary">
          <div className="payment-total">
            <div>{totalLabel}</div>
            <div className="payment-total-number">{formatBRL(total)}</div>
          </div>
          {paymentDate && (
            <p className="payment-date">{paymentDate}</p>
          )}
        </div>
      </div>
    </Card>
  )
}

const CardSelect = ({ creditCards = [], onFormChange, onCardSelect, forceValidation = false }) => {
  const [selectedCheckbox, setSelectedCheckbox] = useState(1)
  const [newCardData, setNewCardData] = useState({ formData: {}, isValid: false }) 

  const formatCardNumber = (digits, brand) => {
    return `•••• •••• •••• ${digits}`
  }

  const getBrandIcon = (brand) => {
    const brandMap = {
      VISA: "visa",
      MASTERCARD: "mastercard",
      MASTER: "mastercard",
      AMEX: "amex",
      "AMERICAN EXPRESS": "amex",
      ELO: "elo",
      HIPERCARD: "hipercard",
    }

    return brandMap[brand?.toUpperCase()] || "blank-card"
  }

  const handleCardSelection = (index, cardId = null) => {
    setSelectedCheckbox(index)
    if (onCardSelect) {
      onCardSelect({
        isNewCard: index === creditCards.length + 1,
        selectedCardId: cardId,
        newCardData: index === creditCards.length + 1 ? newCardData.formData : null,
        isNewCardValid: index === creditCards.length + 1 ? newCardData.isValid : true, 
      })
    }
  }

  const handleNewCardDataChange = (data) => {
    setNewCardData(data) 
    if (onFormChange) {
      onFormChange(data)
    }
    if (selectedCheckbox === creditCards.length + 1) {
      onCardSelect({
        isNewCard: true,
        selectedCardId: null,
        newCardData: data.formData,
        isNewCardValid: data.isValid,
      })
    }
  }

  return (
    <div className="card-select pb-3">
      {creditCards.map((card, index) => (
        <div key={card.id} className="card-select-item">
          <label>
            <input
              checked={selectedCheckbox === index + 1}
              onChange={() => handleCardSelection(index + 1, card.id)}
              type="radio"
              name="payment"
            />
            <Icon id={`icon-${getBrandIcon(card.brand)}`} size={40} />
            <div>{formatCardNumber(card.digits, card.brand)}</div>
          </label>
        </div>
      ))}

      <div className="card-select-item">
        <label>
          <input
            checked={selectedCheckbox === creditCards.length + 1}
            onChange={() => handleCardSelection(creditCards.length + 1)}
            type="radio"
            name="payment"
          />
          <Icon size={40} id="icon-blank-card" />
          <div>Novo cartão de crédito</div>
        </label>
        {selectedCheckbox === creditCards.length + 1 && (
          <CreditForm onFormChange={handleNewCardDataChange} forceValidation={forceValidation} />
        )}
      </div>
    </div>
  )
}

export { FiscalForm, CreditForm, CupomModal, PaymentCard, CardSelect }
